import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";

import { environment } from "../../../environments/environment";
import { AuthService } from "../auth/auth.service";
import { AlertError } from "../error-handler";
import { HelpersService } from "../helper/helpers.service";
import { ProjectService } from "../project/project.service";

@Injectable({
    providedIn: "root"
})
export class PostsService {
    constructor(
        private http: HttpClient,
        private authSvc: AuthService,
        private projectSvc: ProjectService,
        private helpersSvc: HelpersService
    ) {}

    categories(): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/categories";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    hashtags(): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/hashtags";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postInformative(numPage: number, filtros: any): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const params = {
                page: numPage,
                pageSize: environment.pageSize
            };

            if (filtros.categoria !== 0) {
                params["categoria"] = filtros.categoria;
            }
            if (filtros.hashtags !== 0) {
                params["hashtag"] = filtros.hashtags;
            }
            if (filtros.autor !== "") {
                params["autor"] = filtros.autor;
            }

            const url =
                environment.urlDomain + "/api/v1/posts/postInformative?" + this.helpersSvc.serializeData(params);
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postTechnical(numPage: number, filtros: any): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const params = {
                page: numPage,
                pageSize: environment.pageSize
            };

            if (filtros.categoria !== 0) {
                params["categoria"] = filtros.categoria;
            }
            if (filtros.hashtags !== 0) {
                params["hashtag"] = filtros.hashtags;
            }
            if (filtros.autor !== "") {
                params["autor"] = filtros.autor;
            }

            const url = environment.urlDomain + "/api/v1/posts/postTechnical?" + this.helpersSvc.serializeData(params);
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    recentNews(numPage: number, filtros?: any): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const params = {
                page: numPage,
                pageSize: environment.pageSize
            };

            if (filtros && filtros.categoria !== 0) {
                params["categoria"] = filtros.categoria;
            }
            if (filtros && filtros.hashtags !== 0) {
                params["hashtag"] = filtros.hashtags;
            }
            if (filtros && filtros.autor !== "") {
                params["autor"] = filtros.autor;
            }
            if (filtros && filtros.funespana_especial !== "") {
                params["funespana_especial"] = filtros.funespana_especial;
            }

            params["pageSize"] = 3;

            if (this.projectSvc?.projectConfig?.id === 472) {
                params["pageSize"] = 2;
            }
            const url = environment.urlDomain + "/api/v1/posts/postRecentNews?" + this.helpersSvc.serializeData(params);
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    canCreatePost() {
        const accessToken = this.authSvc.getAccessToken();

        const options = {
            headers: new HttpHeaders({
                Authentication: "Bearer " + accessToken,
                "Content-Type": "application/json; charset=utf-8"
            })
        };

        const url = environment.urlDomain + "/api/v1/posts/maxinformative";

        return new Observable<any>((observer) => {
            this.http.post(url, {}, options).subscribe(
                (data) => {
                    console.log("canCreatePost", data);
                    observer.next(data);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postComments(postId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/" + postId + "/postComments";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postLikes(postId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/" + postId + "/postLikes";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    toogleLike(postId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {};

            const url = environment.urlDomain + "/api/v1/posts/toggleLike/" + postId;
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    createComment(postId, textComment) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {
                content: textComment
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/" + postId;
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    editComment(commentId, textComment) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {
                content: textComment
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/edit/" + commentId;
            this.http.put(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    deleteComment(commentId) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/" + commentId;
            this.http.delete(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postCommentsComment(commentId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/" + commentId + "/comments";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    postLikesComment(commentId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/likes/" + commentId;
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    toogleLikeComment(commentId: number): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {};

            const url = environment.urlDomain + "/api/v1/posts/comment/toggleLike/" + commentId;
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    respondComment(postId, commentId, textComment) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {
                content: textComment
            };

            const url = environment.urlDomain + "/api/v1/posts/comment/" + postId + "/response/" + commentId;
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    createPostInformative(formValue) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken
                })
            };

            const formData = new FormData();

            for (const key of Object.keys(formValue)) {
                if (key === "content") {
                    // El contenido HTML necesita tratamiento especial
                    formData.append(key, formValue[key]);
                    continue;
                }

                if (key === "usePersonas") {
                    formData.append("usePersonas", formValue.usePersonas ? "true" : "false");
                    continue;
                }

                if (key === "imagenesCarrusel") {
                    // Asumiendo que formValue.imagenesCarrusel sea boolean: true / false
                    formData.append("imagenesCarrusel", formValue.imagenesCarrusel ? "true" : "false");
                    continue;
                }

                if (key !== "files") {
                    formData.append(key, formValue[key]);
                    continue;
                }

                if (["delegaciones", "puestos", "personas"].includes(key)) {
                    formData.append(key, formValue[key].join(","));
                }

                for (const file of formValue[key]) {
                    formData.append(key, file);
                }
            }

            const url = environment.urlDomain + "/api/v1/posts/";
            this.http.post(url, formData, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    createPost(formValue) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/news";
            this.http.post(url, formValue, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    createNotaTecnica(formValue) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/technical";
            this.http.post(url, formValue, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    toogleFixed(postId) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const body = {};

            const url = environment.urlDomain + "/api/v1/posts/toggleFixed/" + postId;
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    getOneDocument(postId: number): Observable<Blob> {
        const accessToken = this.authSvc.getAccessToken();
        const headers = new HttpHeaders({
            Authentication: "Bearer " + accessToken
        });
        const url = `${environment.urlDomain}/api/v1/posts/getOneDocument/${postId}`;
        return this.http.get(url, { headers, responseType: "blob" });
    }

    deleteOtherComment(commentId) {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/posts/tandem/delete/" + commentId;
            this.http.put(url, {}, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    getListManagerPublicaciones(): Observable<any> {
        return new Observable<any>((oberver) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + "/api/v1/admin/getListManagerPublicaciones";
            this.http.get(url, options).subscribe(
                (response: any) => {
                    oberver.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    getDocument(id: number): Observable<any> {
        const accessToken = this.authSvc.getAccessToken();
        const options = {
            headers: new HttpHeaders({
                Authentication: "Bearer " + accessToken,
                "Content-Type": "application/json; charset=utf-8"
            }),
            responseType: "blob" as "json" // Convertir a 'json' para la asignación de tipos
        };

        const url = `${environment.urlDomain}/api/v1/posts/document/${id}`;

        return this.http
            .get(url, options)
            .pipe(map((response: any) => new Blob([response], { type: "application/octet-stream" })));
    }

    updateDocument(id: number, newDocument): Observable<any> {
        return new Observable<any>((oberver) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + `/api/v1/posts/document/${id}`;
            this.http.put(url, newDocument, options).subscribe(
                (response: any) => {
                    oberver.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    deleteDocument(id: number): Observable<any> {
        return new Observable<any>((oberver) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + `/api/v1/posts/document/${id}`;
            this.http.delete(url, options).subscribe(
                (response: any) => {
                    oberver.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    newDocument(body?): Observable<any> {
        return new Observable<any>((oberver) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                }),
                params: null
            };

            const url = environment.urlDomain + "/api/v1/posts/newDocument/post";
            this.http.post(url, body, options).subscribe(
                (response: any) => {
                    oberver.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }

    getSingleRecentNews(hash: string): Observable<any> {
        return new Observable<any>((observer) => {
            const accessToken = this.authSvc.getAccessToken();

            const options = {
                headers: new HttpHeaders({
                    Authentication: "Bearer " + accessToken,
                    "Content-Type": "application/json; charset=utf-8"
                })
            };

            const url = environment.urlDomain + `/api/v1/posts/getSingleRecentNews/${hash}`;
            this.http.get(url, options).subscribe(
                (response: any) => {
                    observer.next(response);
                },
                (err) => {
                    AlertError.showError(err);
                }
            );
        });
    }
}
